import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Video, FileSearch, LifeBuoy, AlertTriangle, Phone, ShieldCheck } from "lucide-react";
import api from "../api/axios";
import EmergencyLayout from "../components/layout/EmergencyLayout";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";
import { LoadingState } from "../components/ui/states";
import { useLanguage } from "../contexts/LanguageContext";
import { setBystanderAccessToken, withBystanderAuth } from "../utils/bystanderAccess";

const PHONE_RE = /^[6-9]\d{9}$/;

const BystanderReportLookupPage = () => {
  const { qrId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [smsAvailable, setSmsAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDetail, setReportDetail] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);

  useEffect(() => {
    api
      .get("/qr/sms-available")
      .then((res) => setSmsAvailable(Boolean(res.data?.smsAvailable)))
      .catch(() => setSmsAvailable(false));
  }, []);

  const normalizePhone = (value) => value.replace(/\D/g, "").slice(-10);

  const validatePhone = () => {
    const cleaned = normalizePhone(phone);
    if (!PHONE_RE.test(cleaned)) {
      setError(t("public.lookupPhoneInvalid"));
      return null;
    }
    return cleaned;
  };

  const requestOtp = async () => {
    const cleaned = validatePhone();
    if (!cleaned) return;

    setLoading(true);
    setError(null);
    try {
      await api.post("/emergency-report/bystander/otp/request", {
        phone: cleaned,
        qrId: qrId,
      });
      setPhone(cleaned);
      setStep("otp");
    } catch (err) {
      if (err.response?.status === 503) {
        setSmsAvailable(false);
      }
      setError(err.response?.data?.error || t("public.lookupOtpSendFailed"));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    const cleaned = normalizePhone(phone);
    if (!PHONE_RE.test(cleaned)) {
      setError(t("public.lookupPhoneInvalid"));
      return;
    }
    if (!/^\d{6}$/.test(otp)) {
      setError(t("public.lookupOtpInvalid"));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await api.post("/emergency-report/bystander/otp/verify", {
        phone: cleaned,
        otp,
        qrId: qrId,
      });

      if (res.data?.accessToken) {
        setBystanderAccessToken(null, res.data.accessToken);
        (res.data.reports || []).forEach((r) =>
          setBystanderAccessToken(r.id, res.data.accessToken),
        );
      }

      const list = (res.data?.reports || []).filter(
        (r) => !r.qrId || r.qrId.toUpperCase() === qrId.toUpperCase(),
      );
      setReports(list);
      setStep("reports");
      if (list.length === 0) {
        setError(t("public.lookupNoReports"));
      }
    } catch (err) {
      setError(err.response?.data?.error || t("public.lookupOtpVerifyFailed"));
    } finally {
      setLoading(false);
    }
  };

  const loadReportDetail = async (report) => {
    setLoading(true);
    setError(null);
    setSelectedReport(report);
    setReportDetail(null);
    setVideoUrl(null);
    try {
      const res = await api.get(
        `/emergency-report/${report.id}`,
        withBystanderAuth({}, report.id),
      );
      setReportDetail(res.data);
      setStep("detail");

      if (report.hasVideo) {
        const videoRes = await api.get(
          `/emergency-report/${report.id}/video`,
          withBystanderAuth({}, report.id),
        );
        setVideoUrl(videoRes.data?.videoUrl || null);
      }
    } catch (err) {
      setError(err.response?.data?.error || t("public.lookupFailed"));
    } finally {
      setLoading(false);
    }
  };

  const openRescue = (reportId) => {
    navigate(`/${qrId}/rescue?reportId=${reportId}`);
  };

  if (loading) {
    return (
      <EmergencyLayout title={t("public.lookupTitle")} showBack onBack={() => navigate(`/${qrId}/public`)}>
        <LoadingState label={t("public.lookupLoading")} />
      </EmergencyLayout>
    );
  }

  return (
    <EmergencyLayout
      title={t("public.lookupTitle")}
      subtitle={t("public.lookupSubtitle")}
      showBack
      onBack={() => navigate(`/${qrId}/public`)}
      showLogo
    >
      <div className="space-y-4 animate-fade-in max-w-lg mx-auto">
        <div className="bg-primary-light border border-primary-border rounded-lg p-4 flex gap-3">
          <FileSearch size={22} className="text-primary shrink-0 mt-0.5" aria-hidden />
          <p className="text-body text-foreground leading-relaxed">{t("public.lookupIntro")}</p>
        </div>

        {!smsAvailable && (
          <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>{t("public.lookupSmsUnavailable")}</span>
          </div>
        )}

        {step === "phone" && (
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            <div>
              <Label htmlFor="phone">{t("public.lookupPhone")}</Label>
              <div className="relative mt-1">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  className="w-full min-h-12 border border-input rounded-lg pl-9 pr-3 py-2.5 text-body focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20"
                  placeholder={t("public.lookupPhonePlaceholder")}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">{t("public.lookupPhoneHint")}</p>
            </div>
            {error && <ErrorBox message={error} />}
            <Button variant="primary" fullWidth onClick={requestOtp} disabled={!smsAvailable}>
              {t("public.lookupSendOtp")}
            </Button>
          </div>
        )}

        {step === "otp" && (
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg p-3">
              <ShieldCheck size={16} />
              <span>{t("public.lookupOtpSent")} {phone}</span>
            </div>
            <div>
              <Label htmlFor="otp">{t("public.lookupOtp")}</Label>
              <Input
                id="otp"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="font-mono tracking-widest"
                placeholder="000000"
              />
            </div>
            {error && <ErrorBox message={error} />}
            <Button variant="primary" fullWidth onClick={verifyOtp}>
              {t("public.lookupVerifyOtp")}
            </Button>
            <Button variant="outline" fullWidth onClick={() => { setStep("phone"); setError(null); }}>
              {t("public.lookupChangePhone")}
            </Button>
          </div>
        )}

        {step === "reports" && reports.length > 0 && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <p className="text-sm font-bold text-foreground">{t("public.lookupYourReports")}</p>
            </div>
            <div className="divide-y divide-border">
              {reports.map((report) => (
                <div key={report.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground capitalize">{report.emergencyType}</p>
                    <p className="text-xs text-muted-foreground">
                      {report.createdAt ? new Date(report.createdAt).toLocaleString() : ""}
                      {report.hasVideo ? ` · ${t("public.lookupHasVideo")}` : ""}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => loadReportDetail(report)}>
                      {t("public.lookupView")}
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => openRescue(report.id)}>
                      {t("public.continueRescue")}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === "detail" && reportDetail && (
          <div className="bg-card border border-emerald-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100">
              <p className="text-sm font-bold text-emerald-900">{t("public.lookupFound")}</p>
              <p className="text-xs text-emerald-700 mt-0.5">
                {reportDetail.emergencyType}
                {reportDetail.createdAt ? ` · ${new Date(reportDetail.createdAt).toLocaleString()}` : ""}
              </p>
            </div>
            <div className="p-4 space-y-3">
              {reportDetail.address && (
                <p className="text-sm text-foreground">
                  <span className="font-semibold">{t("public.lookupLocation")}: </span>
                  {reportDetail.address}
                </p>
              )}
              {videoUrl ? (
                <div>
                  <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                    <Video size={16} />
                    {t("public.lookupVideo")}
                  </p>
                  <video
                    src={videoUrl}
                    controls
                    playsInline
                    className="w-full rounded-lg border border-border bg-black max-h-64"
                  />
                </div>
              ) : selectedReport?.hasVideo ? (
                <LoadingState label={t("public.lookupLoadingVideo")} />
              ) : (
                <p className="text-sm text-muted-foreground italic">{t("public.lookupNoVideo")}</p>
              )}
              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Button
                  variant="emergency"
                  fullWidth
                  onClick={() => openRescue(selectedReport?.id || reportDetail.id)}
                >
                  <LifeBuoy size={16} />
                  {t("public.continueRescue")}
                </Button>
                <Button variant="outline" fullWidth onClick={() => setStep("reports")}>
                  {t("public.lookupBackToList")}
                </Button>
              </div>
            </div>
          </div>
        )}

        {error && step !== "phone" && step !== "otp" && <ErrorBox message={error} />}
      </div>
    </EmergencyLayout>
  );
};

function ErrorBox({ message }) {
  return (
    <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg p-3">
      <AlertTriangle size={16} className="shrink-0 mt-0.5" />
      <span>{message}</span>
    </div>
  );
}

export default BystanderReportLookupPage;
